import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  IconAdd,
  IconChannelsOrder,
  IconEdit,
  IconGridView,
  IconGroups,
  IconListView,
  IconOrder,
} from '../../../../../assets/popup-nav-icons';
import { PopUpNavLink } from '../../../../../components/popup-nav';
import { useInstallationContext } from '../../../../../context/installation';
import { InstallationAccessType, UserGroup, UserPermissionType } from '../../../../../data-access/gql-types/graphql';
import { useUserPermissions } from '../../../../../hooks/user/use-user-permissions';
import { ROUTES } from '../../../../../routes/routes';
import { useGroupsStore } from '../../../store/groups';
import { useAddDevice } from '../../device-add/hooks/use-add-device';

export const useGroups = (activeGroup: UserGroup) => {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  const { t: tg } = useTranslation('groups');
  const { selectedInstallation } = useInstallationContext();
  const { permissions } = useUserPermissions();
  const { addDevice } = useAddDevice();
  const isListView = useGroupsStore((s) => s.isListView);
  const toggleListView = useGroupsStore((s) => s.toggleListView);

  const popUpNavLinks = useMemo((): PopUpNavLink[] => {
    const navLinks: PopUpNavLink[] = [
      { onClick: () => navigate(ROUTES.GroupList()), label: tg('allGroups'), hasLineBelow: true, icon: <IconGroups /> },
      {
        onClick: () => navigate(ROUTES.GroupSortChannels(isListView)),
        label: t('channelsOrder'),
        icon: <IconChannelsOrder />,
      },
      {
        onClick: () => navigate(ROUTES.GroupSort()),
        label: t('groupsOrder'),
        hasLineBelow: true,
        icon: <IconOrder />,
      },
      {
        onClick: toggleListView,
        label: isListView ? t('gridView') : t('listView'),
        icon: isListView ? <IconGridView /> : <IconListView />,
      },
      { onClick: addDevice, label: t('addDevice'), icon: <IconAdd /> },
    ];

    if (activeGroup && selectedInstallation) {
      const { accessType } = selectedInstallation;

      if (permissions[UserPermissionType.GroupCreate]) {
        navLinks.unshift({
          onClick: () => navigate(ROUTES.GroupCreate()),
          label: t('newGroup'),
          icon: <IconAdd />,
        });
      }
      if (
        [InstallationAccessType.Owner, InstallationAccessType.Admin].includes(accessType) ||
        (accessType === InstallationAccessType.User && !activeGroup.group.isPublicGroup)
      ) {
        if (
          (activeGroup.group.isPublicGroup && permissions[UserPermissionType.PublicGroupEdit]) ||
          (!activeGroup.group.isPublicGroup && permissions[UserPermissionType.GroupEdit])
        )
          navLinks.unshift({
            onClick: () =>
              navigate(
                activeGroup.group.isDefaultGroup
                  ? ROUTES.FavouriteGroupEdit(activeGroup.id)
                  : ROUTES.GroupEdit(activeGroup.id),
              ),
            label: t('groupEdit', { groupName: activeGroup.group.name }),
            hasLineBelow: true,
            icon: <IconEdit />,
          });
      }
    }

    return navLinks;
  }, [activeGroup, isListView, selectedInstallation?.accessType, permissions]);

  return {
    popUpNavLinks,
  };
};
